// Feelrooty Global API (modern, minimal)
(() => {
  const root = window;
  const store = (root.store = root.store || {});
  // Debug flags: enable logs by setting window.store.debug = true, or via inline script before this file loads
  // Also allow locking debug based on a hardcoded build-time flag (set to false in production if desired)
  const DEBUG_DEFAULT = false; // change to true to ship debugging by default
  if (typeof store.debug === 'undefined') store.debug = DEBUG_DEFAULT;
  // Mute flags to reduce noisy logs (opt-in to see them)
  if (typeof store.muteNavbar === 'undefined') store.muteNavbar = true;
  if (typeof store.muteEventsEmit === 'undefined') store.muteEventsEmit = true;
  store.log = (...args) => {
    try {
      if (!store || !store.debug) return;
      const first = (args && args.length ? args[0] : undefined);
      if (typeof first === 'string') {
        // Silence verbose navbar logs when muted
        if (store.muteNavbar && first.indexOf('[navbar]') === 0) return;
        // Silence event emission spam when muted
        if (store.muteEventsEmit && first.indexOf('[events.emit]') === 0) return;
      }
      console.log('[store]', ...args);
    } catch (_) {}
  };

  // Resolve dotted path to a function on window
  const resolve = (path) => {
    if (!path) return null;
    const parts = String(path).split('.');
    let ctx = root;
    for (let i = 0; i < parts.length - 1; i += 1) {
      ctx = ctx && ctx[parts[i]];
      if (!ctx) return null;
    }
    const name = parts[parts.length - 1];
    const fn = ctx && ctx[name];
    return typeof fn === 'function' ? { ctx, fn } : null;
  };

  // store.call('Namespace.fn', [eventOrSelector], [element])
  store.call = (path, eventOrSelector = null, element = null) => {
    try {
      const target = resolve(path);
      if (!target) return;

      // Normalize arguments: allow 0 args, a selector string, an Event, or an element
      let ev = null;
      let el = null;
      if (typeof eventOrSelector === 'string' && !element) {
        el = document.querySelector(eventOrSelector);
      } else {
        if (eventOrSelector && typeof Event !== 'undefined' && eventOrSelector instanceof Event) {
          ev = eventOrSelector;
        } else if (eventOrSelector && eventOrSelector.nodeType === 1) {
          el = eventOrSelector;
        }
        if (!el && element && element.nodeType === 1) {
          el = element;
        }
      }

      return target.fn.call(target.ctx, ev, el);
    } catch (e) {
      store.log('[warn][store.call] error', e);
    }
  };

  // store.emit('name', detail?, target?)
  store.emit = (name, detail = undefined, target = document) => {
    try {
      if (String(name).startsWith('store:') && window.storeEvents && window.storeEvents.emit) {
        window.storeEvents.emit(name, detail, target);
        return;
      }
    } catch (_) {}
    try {
      target && target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    } catch (e) {
      store.log('[warn][store.emit] error', e);
    }
  };

  // Removed demo namespace (keep runtime minimal)

  // Attribute-based analytics capture moved to assets/events.js (data-track / data-ph-event)

  // PostHog is expected to be initialized via theme settings/custom head HTML
  // Store events bridge (optional): emit cart lifecycle events
  // Bridge to app events (non-module safe)
  const __emit = (name, detail) => {
    try {
      if (window.storeEvents && window.storeEvents.emit) {
        if (!name) return;
        window.storeEvents.emit(name, detail, document);
      }
    } catch (_) {}
  };
  
  // Cart namespace
  store.cart = store.cart || {};
  
  // store.cart.add(variantId, quantity = 1, properties = undefined, sellingPlanId = undefined)
  store.cart.add = async (variantId, quantity = 1, properties, sellingPlanId) => {
    if (!variantId) return Promise.reject(new Error('Missing variantId'));
    try {
      const idNum = (() => { try { return Number(variantId); } catch (_) { return variantId; } })();
      const qtyNum = (() => { try { return Math.max(1, Number(quantity) || 1); } catch (_) { return 1; } })();
      try { __emit(window.storeEvents && window.storeEvents.Events && window.storeEvents.Events.cart && window.storeEvents.Events.cart.add, { id: idNum, quantity: qtyNum, properties }); } catch (_) {}
      const body = { id: idNum, quantity: qtyNum };
      if (properties && typeof properties === 'object') body.properties = properties;
      if (sellingPlanId) body.selling_plan = String(sellingPlanId);
      store.log('[store.cart.add] POST /cart/add.js', body);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data && data.description || 'Add to cart failed');
      store.emit('cart:added', data);
      try {
        // Notify listeners; let cart-aware components fetch fresh cart state
        __emit(window.storeEvents && window.storeEvents.Events && window.storeEvents.Events.cart && window.storeEvents.Events.cart.added, { item: data });
        __emit(window.storeEvents && window.storeEvents.Events && window.storeEvents.Events.cart && window.storeEvents.Events.cart.update, {});
      } catch (_) {}
      return data;
    } catch (e) {
      store.log('[warn][store.cart.add] error', e);
      try { __emit(window.storeEvents && window.storeEvents.Events && window.storeEvents.Events.cart && window.storeEvents.Events.cart.error, { message: e && e.message, id: variantId }); } catch (_) {}
      throw e;
    }
  };

  // store.cart.addFromElement(event, element)
  // Reads variant from the nearest [data-product-id] wrapper when present, or falls back to
  // select/input[name="id"] in the nearest form/section/document. Also reads quantity from an input or
  // data-quantity, collects optional properties[name] inputs, and detects a selected selling_plan (Appstle-compatible)
  store.cart.addFromElement = async (event, element) => {
    try {
      const el = element || (event && event.target);
      if (!el) return;
      // Stop event immediately to prevent double handling
      event && event.preventDefault && event.preventDefault();
      event && event.stopPropagation && event.stopPropagation();
      event && event.stopImmediatePropagation && event.stopImmediatePropagation();

      const wrap = el.closest && el.closest('[data-product-id]');
      // Check clicked element first, then wrapper
      let variantId = (el.getAttribute && el.getAttribute('data-variant-id')) || (wrap && wrap.getAttribute && wrap.getAttribute('data-variant-id'));
      let quantity = 1;
      let hasExplicitQty = false;
      try {
        const qtyAttr = el.getAttribute && el.getAttribute('data-quantity');
        if (qtyAttr) {
          quantity = Math.max(1, parseInt(qtyAttr, 10) || 1);
          hasExplicitQty = true; // Don't override with input if button has explicit quantity
          store.log('[store.cart.addFromElement] using explicit qty from button:', quantity);
        }
        // Only look for quantity input if wrap exists AND no explicit quantity on button
        if (wrap && !hasExplicitQty) {
          const qtyInput = wrap.querySelector('input[name="quantity"], input[type="number"][name="quantity"]');
          if (qtyInput && qtyInput.value) {
            quantity = Math.max(1, parseInt(qtyInput.value, 10) || quantity);
            store.log('[store.cart.addFromElement] using qty from input:', quantity);
          }
        }
      } catch (_) {}
      // Build scope roots (inner to outer) for robust fallbacks
      const scopeRoots = [];
      try { const form = el.closest && el.closest('form'); if (form) scopeRoots.push(form); } catch (_) {}
      try { if (wrap) scopeRoots.push(wrap); } catch (_) {}
      try { const sectionRoot = el.closest && el.closest('.shopify-section'); if (sectionRoot) scopeRoots.push(sectionRoot); } catch (_) {}
      if (!scopeRoots.length) scopeRoots.push(document);

      // If wrapper/variant not present, fall back to nearest [data-variant-id] or select/input[name=id]
      if (!variantId) {
        for (let i = 0; i < scopeRoots.length && !variantId; i += 1) {
          const s = scopeRoots[i];
          try {
            const vEl = s.querySelector && s.querySelector('[data-variant-id]');
            if (vEl && vEl.getAttribute) variantId = vEl.getAttribute('data-variant-id');
          } catch (_) {}
        }
        for (let i = 0; i < scopeRoots.length && !variantId; i += 1) {
          const s = scopeRoots[i];
          try {
            const idInput = s.querySelector && s.querySelector('input[name="id"], select[name="id"]');
            if (idInput) variantId = idInput.value || idInput.getAttribute('value');
          } catch (_) {}
        }
        if (!variantId) {
          try { variantId = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariantId) || (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.variants && window.ShopifyAnalytics.meta.product.variants[0] && window.ShopifyAnalytics.meta.product.variants[0].id) || null; } catch (_) { variantId = null; }
        }
      }
      if (!variantId) { store.log('[warn][store.cart.addFromElement] variant id not found'); return; }
      // Determine a scoping root to look for form fields
      const scope = (() => { if (scopeRoots.length) return scopeRoots[0]; return document; })();

      // Collect line item properties: inputs like properties[Key]
      let properties = undefined;
      try {
        const propInputs = Array.from(scope.querySelectorAll('input[name^="properties["], textarea[name^="properties["]'));
        if (propInputs.length) {
          const obj = {};
          propInputs.forEach((inp) => {
            try {
              const name = inp.getAttribute('name') || '';
              const m = name.match(/^properties\[(.*)\]$/);
              if (!m || !m[1]) return;
              let value = '';
              const type = (inp.getAttribute('type') || '').toLowerCase();
              if (type === 'checkbox') {
                if (!inp.checked) return; // unchecked checkboxes omitted
                value = inp.value || 'on';
              } else if (type === 'radio') {
                if (!inp.checked) return; // only checked radios
                value = inp.value || '';
              } else {
                value = inp.value || '';
              }
              obj[m[1]] = value;
            } catch (_) {}
          });
          if (Object.keys(obj).length) properties = obj;
        }
      } catch (_) {}

      // Detect selling plan (Appstle) across multiple plausible roots
      // Skip if element has explicit data-force-one-time attribute (used by cart upsells)
      let sellingPlanId = undefined;
      const forceOneTime = el.hasAttribute && el.hasAttribute('data-force-one-time');
      if (!forceOneTime) {
        try {
          const searchRoots = [...scopeRoots, document];
          for (let i = 0; i < searchRoots.length && !sellingPlanId; i += 1) {
            const rootEl = searchRoots[i];
            try {
              // 1) Checked radio determines one-time vs subscription
              const spChecked = rootEl.querySelector && rootEl.querySelector('input[name="selling_plan"]:checked');
              const radioVal = spChecked ? String(spChecked.value || '').trim() : null;
              const oneTimeSelected = spChecked && radioVal === '';
              if (radioVal && radioVal !== '') { sellingPlanId = radioVal; break; }
              // 2) Active dropdown inside Appstle widget
              const activeWrap = rootEl.querySelector && rootEl.querySelector('.appstle_subscription_wrapper .appstle_subscription_wrapper_option.appstle_include_dropdown.appstle-active-option');
              const sel = activeWrap && activeWrap.querySelector ? activeWrap.querySelector('select.appstle_select') : null;
              const selVal = sel && sel.value ? String(sel.value).trim() : '';
              if (!oneTimeSelected && selVal !== '') { sellingPlanId = selVal; break; }
              // 3) Hidden mirror input used by Appstle
              const hiddenSp = rootEl.querySelector && rootEl.querySelector('input.appstle-selectedSellingPlan, input[name="properties[__appstle-selected-selling-plan]"]');
              const hiddenVal = hiddenSp && hiddenSp.value ? String(hiddenSp.value).trim() : '';
              if (!oneTimeSelected && hiddenVal !== '') { sellingPlanId = hiddenVal; break; }
              // 4) Widget attribute as last resort
              const widget = rootEl.querySelector && rootEl.querySelector('.appstle_sub_widget[data-selected-selling-plan]');
              const attrVal = widget && widget.getAttribute ? String(widget.getAttribute('data-selected-selling-plan') || '').trim() : '';
              if (!oneTimeSelected && attrVal !== '') { sellingPlanId = attrVal; break; }
            } catch (_) {}
          }
        } catch (_) {}
      }

      store.log('[store.cart.addFromElement] calling add()', { variantId, quantity, properties, sellingPlanId });
      const result = await store.cart.add(variantId, quantity, properties, sellingPlanId);
      store.log('[store.cart.addFromElement] add() result', result);
      // Optionally open cart dialog if present
      try { if (document.getElementById('cart')) store.dialog.open('cart'); } catch (_) {}
      // Fetch fresh cart once and broadcast with payload for fast client updates (shipping bar, counters)
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        applyCartUI(cart);
        try { __emit('store:cart:update', { cart }); } catch (_) {}
      } catch (_) {}
      return result;
    } catch (e) {
      store.log('[warn][store.cart.addFromElement] error', e);
    }
  };

  // Live UI updates on cart changes without relying solely on section re-render
  const formatMoney = (cents) => {
    try {
      const value = typeof cents === 'number' ? Math.round(cents) : parseInt(String(cents||0), 10) || 0;
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt = window.Shopify.money_format || undefined;
        return window.Shopify.formatMoney(value, fmt);
      }
      return (value/100).toFixed(2);
    } catch (_) {
      try { return (Number(cents||0)/100).toFixed(2); } catch (__) { return String(cents); }
    }
  };

  const applyCartUI = (cart) => {
    try {
      if (!cart) return;
      // Global state/attributes for passive components
      try { root.store = root.store || {}; root.store.cartState = cart; } catch (_) {}
      try { document.documentElement.setAttribute('data-cart-total-cents', String(cart.total_price || 0)); } catch (_) {}
      try { document.documentElement.setAttribute('data-cart-item-count', String(cart.item_count || 0)); } catch (_) {}
      try { document.documentElement.setAttribute('data-cart', JSON.stringify(cart)); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cartstate:change', { detail: { cart }, bubbles: true })); } catch (_) {}
      // Update all cart count slots
      try {
        const counters = document.querySelectorAll('[data-role="cart-count"]');
        counters && counters.forEach && counters.forEach((el) => { try { el.textContent = `(${cart.item_count||0})`; } catch (_) {} });
      } catch (_) {}
      // Update subtotal slots
      try {
        const subs = document.querySelectorAll('#cart-subtotal');
        subs && subs.forEach && subs.forEach((el) => { try { el.textContent = formatMoney(cart.total_price); } catch (_) {} });
      } catch (_) {}
      // Update original subtotal slots when present
      try {
        const computeOriginal = (c) => {
          try {
            if (!c || !Array.isArray(c.items)) return Number(c && c.total_price) || 0;
            let sum = 0;
            (c.items || []).forEach((it) => {
              try {
                const qty = Number(it && it.quantity) || 0;
                const finalLine = Number((it && (it.final_line_price != null ? it.final_line_price : it.line_price)) || 0);
                let origLine = Number((it && it.original_line_price != null ? it.original_line_price : finalLine) || 0);
                try {
                  const plan = it && it.selling_plan_allocation;
                  const cmpUnit = plan && plan.compare_at_price != null ? Number(plan.compare_at_price) : 0;
                  if (cmpUnit > 0) {
                    const cmpLine = cmpUnit * (qty || 1);
                    if (cmpLine > origLine) origLine = cmpLine;
                  }
                } catch (_) {}
                if (origLine < finalLine) origLine = finalLine;
                sum += origLine;
              } catch (_) {}
            });
            return sum;
          } catch (_) { return Number(c && c.total_price) || 0; }
        };
        const original = computeOriginal(cart);
        const os = document.querySelectorAll('#cart-subtotal-original');
        os && os.forEach && os.forEach((el) => {
          try {
            if (original > (cart.total_price || 0)) {
              el.textContent = formatMoney(original);
              el.removeAttribute('hidden');
            } else {
              el.setAttribute('hidden','');
            }
          } catch (_) {}
        });
      } catch (_) {}
      // Update visible cart rows (quantity and line price) for immediate feedback
      try {
        const byKey = Object.create(null);
        const byVariant = Object.create(null);
        const byVariantPlan = Object.create(null); // key: "variantId:planIdOrEmpty"
        (cart.items || []).forEach((it) => {
          try { if (it && it.key) byKey[String(it.key)] = it; } catch (_) {}
          try {
            if (it && it.variant_id != null) {
              const vid = String(it.variant_id);
              byVariant[vid] = byVariant[vid] || [];
              byVariant[vid].push(it);
              const planId = (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.id) ? String(it.selling_plan_allocation.selling_plan.id) : '';
              const k = vid + ':' + planId;
              byVariantPlan[k] = it;
            }
          } catch (_) {}
        });
        const drawers = document.querySelectorAll('cart-drawer');
        drawers && drawers.forEach && drawers.forEach((cd) => {
          try {
            // Only update cart item articles, not upsell buttons (which also have data-variant-id)
            const rows = cd.querySelectorAll && cd.querySelectorAll('#cart-items article[data-line-key]');
            rows && rows.forEach && rows.forEach((row) => {
              try {
                const key = row.getAttribute('data-line-key') || '';
                const vId = row.getAttribute('data-variant-id') || '';
                // Optional: detect selected plan from row's select
                let selPlan = '';
                try {
                  const sel = row.querySelector('select[data-selling-plan]');
                  selPlan = (sel && (sel.getAttribute('data-selected-plan') || sel.value || '') || '').trim();
                } catch (_) { selPlan = ''; }
                let item = null;
                if (key && byKey[key]) item = byKey[key];
                if (!item && vId) {
                  if (selPlan !== '' && byVariantPlan[vId + ':' + selPlan]) {
                    item = byVariantPlan[vId + ':' + selPlan];
                  } else if (byVariant[vId] && byVariant[vId].length) {
                    // Fallback: match first by variant id
                    item = byVariant[vId][0];
                  }
                }
                if (!item) return;
                // Quantity
                try {
                  const qtyInput = row.querySelector('input[name="quantity"]');
                  if (qtyInput) qtyInput.value = String(item.quantity || 0);
                  row.setAttribute('data-quantity', String(item.quantity || 0));
                } catch (_) {}
                // Line price
                try {
                  const priceEl = row.querySelector && row.querySelector('[data-role="price"]');
                  if (priceEl) priceEl.innerHTML = `<span class="money">${formatMoney(item.final_line_price)}</span>`;
                } catch (_) {}
              } catch (_) {}
            });
          } catch (_) {}
        });
      } catch (_) {}
      // Share latest state with any existing cart-drawer instances (helps nearby shipping bars too)
      try {
        const drawers = document.querySelectorAll('cart-drawer');
        drawers && drawers.forEach && drawers.forEach((cd) => {
          try { cd.setAttribute('data-cart', JSON.stringify(cart)); } catch (_) {}
          try { cd.dispatchEvent(new CustomEvent('cartstate:change', { detail: { cart }, bubbles: true })); } catch (_) {}
        });
      } catch (_) {}
    } catch (_) {}
  };

  try {
    document.addEventListener('store:cart:update', async (ev) => {
      try {
        if (ev && ev.detail && ev.detail.cart) {
          applyCartUI(ev.detail.cart);
          return;
        }
        // Fallback: fetch cart and apply
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        applyCartUI(cart);
      } catch (_) {}
    }, true);
  } catch (_) {}

  // Attribute one-liner: any clickable with data-add-to-cart will add the closest product's selected/first variant
  document.addEventListener('click', (ev) => {
    try {
      const el = ev.target && ev.target.closest && ev.target.closest('[data-add-to-cart]');
      if (!el) return;
      store.cart.addFromElement(ev, el);
    } catch (e) {
      store.log('[warn][store.cart] delegation error', e);
    }
  }, true);

  // Quantity stepper delegation: buttons with data-qty="inc" or data-qty="dec" adjust nearest input[name="quantity"]
  document.addEventListener('click', (ev) => {
    try {
      const btn = ev.target && ev.target.closest && ev.target.closest('[data-qty]');
      if (!btn) return;
      const dir = (btn.getAttribute && btn.getAttribute('data-qty')) || '';
      if (dir !== 'inc' && dir !== 'dec') return;
      ev.preventDefault && ev.preventDefault();
      ev.stopPropagation && ev.stopPropagation();
      const scope = (btn.closest && (btn.closest('form') || btn.closest('[data-product-id]'))) || document;
      const input = scope && scope.querySelector && scope.querySelector('input[name="quantity"], input[type="number"][name="quantity"]');
      if (!input) return;
      const min = (() => { try { return Math.max(1, parseInt(input.getAttribute('min') || '1', 10) || 1); } catch (_) { return 1; } })();
      const max = (() => { try { const m = input.getAttribute('max'); return m != null && m !== '' ? (parseInt(m, 10) || null) : null; } catch (_) { return null; } })();
      const step = (() => { try { return Math.max(1, parseInt(input.getAttribute('step') || '1', 10) || 1); } catch (_) { return 1; } })();
      let val = (() => { try { return Math.max(min, parseInt(input.value || String(min), 10) || min); } catch (_) { return min; } })();
      if (dir === 'inc') val += step; else if (dir === 'dec') val -= step;
      if (val < min) val = min;
      if (max != null && val > max) val = max;
      input.value = String(val);
      try { input.dispatchEvent(new Event('input', { bubbles: true })); } catch (_) {}
      try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) {}
    } catch (e) {
      store.log('[warn][qty.stepper] error', e);
    }
  }, true);

  // Quantity direct-set delegation: buttons with data-qty-set="N" set the nearest quantity input to N
  document.addEventListener('click', (ev) => {
    try {
      const btn = ev.target && ev.target.closest && ev.target.closest('button[data-qty-set], button[data-quantity]:not([data-add-to-cart]), [role="button"][data-qty-set], [role="button"][data-quantity]:not([data-add-to-cart])');
      if (!btn) return;
      // Do not hijack clicks inside cart drawer UI
      try { if (btn.closest && btn.closest('cart-drawer')) return; } catch (_) {}
      // Let dedicated quantity components handle their own logic
      try { if (btn.closest && (btn.closest('quantity-cards') || btn.closest('quantity-selector') || btn.closest('store-quantity-sync'))) return; } catch (_) {}
      ev.preventDefault && ev.preventDefault();
      ev.stopPropagation && ev.stopPropagation();
      const raw = (btn.getAttribute && (btn.getAttribute('data-qty-set') || btn.getAttribute('data-quantity')));
      let next = (() => { try { return Math.max(1, parseInt(raw || '1', 10) || 1); } catch (_) { return 1; } })();
      // Resolve target input: explicit selector > nearest form or product wrapper(s) (inner to outer)
      const explicitSel = btn.getAttribute && btn.getAttribute('data-qty-target');
      const scopeRoots = [];
      try { const form = btn.closest && btn.closest('form'); if (form) scopeRoots.push(form); } catch (_) {}
      try {
        let probe = btn.closest && btn.closest('[data-product-id]');
        // Push inner to outer [data-product-id] ancestors so we can search wider if needed
        while (probe) {
          scopeRoots.push(probe);
          const nextProbe = probe.parentElement && probe.parentElement.closest && probe.parentElement.closest('[data-product-id]');
          if (nextProbe && nextProbe !== probe) { probe = nextProbe; } else { break; }
        }
      } catch (_) {}
      if (!scopeRoots.length) scopeRoots.push(document);

      // Find input in the first scope that has it
      let input = null;
      if (explicitSel) {
        try { input = document.querySelector(explicitSel); } catch (_) { input = null; }
      }
      if (!input) {
        for (let i = 0; i < scopeRoots.length; i += 1) {
          const s = scopeRoots[i];
          try {
            const found = s && s.querySelector && s.querySelector('input[name="quantity"], input[type="number"][name="quantity"]');
            if (found) { input = found; break; }
          } catch (_) {}
        }
      }
      if (input) {
        const min = (() => { try { return Math.max(1, parseInt(input.getAttribute('min') || '1', 10) || 1); } catch (_) { return 1; } })();
        const max = (() => { try { const m = input.getAttribute('max'); return m != null && m !== '' ? (parseInt(m, 10) || null) : null; } catch (_) { return null; } })();
        if (next < min) next = min;
        if (max != null && next > max) next = max;
        input.value = String(next);
        try { input.dispatchEvent(new Event('input', { bubbles: true })); } catch (_) {}
        try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) {}
      } else {
        // No input found: reflect on nearest add-to-cart button as data-quantity
        try {
          let addBtn = null;
          for (let i = 0; i < scopeRoots.length; i += 1) {
            const s = scopeRoots[i];
            try { addBtn = s && s.querySelector && s.querySelector('[data-add-to-cart]'); } catch (_) { addBtn = null; }
            if (addBtn) break;
          }
          if (!addBtn) {
            // As a last resort, try matching by product id globally
            try {
              const nearestPidRoot = btn.closest && btn.closest('[data-product-id]');
              const pid = nearestPidRoot && nearestPidRoot.getAttribute && nearestPidRoot.getAttribute('data-product-id');
              if (pid) addBtn = document.querySelector('[data-product-id="' + pid + '"] [data-add-to-cart]');
            } catch (_) {}
          }
          if (addBtn) addBtn.setAttribute('data-quantity', String(next));
        } catch (_) {}
      }
      // Optional UI state: mark pressed within the same parent container
      try {
        const parent = btn.parentElement;
        if (parent) {
          const siblings = parent.querySelectorAll('[data-qty-set]');
          siblings && siblings.forEach && siblings.forEach((el) => { el.removeAttribute('data-selected'); el.setAttribute('aria-pressed', 'false'); });
          btn.setAttribute('data-selected', '');
          btn.setAttribute('aria-pressed', 'true');
        }
      } catch (_) {}
    } catch (e) {
      store.log('[warn][qty.set] error', e);
    }
  }, true);

  // Blog articles pagination: prev/next arrows that update ?page=N and refresh current section
  document.addEventListener('click', (ev) => {
    try {
      const btn = ev.target && ev.target.closest && ev.target.closest('[data-blog-pagination] [data-action]');
      if (!btn) return;
      const wrap = btn.closest && btn.closest('[data-blog-pagination]');
      if (!wrap) return;
      ev.preventDefault && ev.preventDefault();
      ev.stopPropagation && ev.stopPropagation();
      const action = (btn.getAttribute && btn.getAttribute('data-action')) || '';
      const param = ((wrap.getAttribute && wrap.getAttribute('data-param')) || 'page').toLowerCase();
      const pages = (() => { try { return Math.max(1, parseInt(wrap.getAttribute('data-pages') || '1', 10) || 1); } catch (_) { return 1; } })();
      const current = (() => { try { return Math.max(1, parseInt(wrap.getAttribute('data-page') || '1', 10) || 1); } catch (_) { return 1; } })();
      let next = current;
      if (action === 'prev') next = current - 1; else if (action === 'next') next = current + 1;
      if (next < 1) next = 1; if (next > pages) next = pages;
      if (next === current) return;
      // Try to sync count and disabled state immediately for responsiveness
      try {
        const countEl = wrap.querySelector('[data-blog-pagination-count]');
        if (countEl) countEl.textContent = String(next) + ' / ' + String(pages);
        const prevBtn = wrap.querySelector('[data-action="prev"]');
        const nextBtn = wrap.querySelector('[data-action="next"]');
        if (prevBtn) { const dis = next <= 1 || pages <= 1; prevBtn.toggleAttribute('disabled', dis); prevBtn.classList.toggle('pointer-events-none', dis); prevBtn.classList.toggle('opacity-40', dis); }
        if (nextBtn) { const dis2 = next >= pages || pages <= 1; nextBtn.toggleAttribute('disabled', dis2); nextBtn.classList.toggle('pointer-events-none', dis2); nextBtn.classList.toggle('opacity-40', dis2); }
      } catch (_) {}
      const url = new URL(window.location.href);
      if (next <= 1) { url.searchParams.delete(param); } else { url.searchParams.set(param, String(next)); }
      try { window.history && window.history.pushState && window.history.pushState({}, '', url.toString()); }
      catch (_) { window.location.assign(url.toString()); return; }
      try {
        // Pull latest page/pages from provider summary in this section so arrows can disable properly
      try {
        const sectionIdAttr = (wrap.getAttribute && wrap.getAttribute('data-section-id')) || '';
        let sectionRoot = null;
        if (sectionIdAttr) {
          // Prefer explicit id when provided
          sectionRoot = document.getElementById(sectionIdAttr) || document.querySelector('#' + CSS.escape(sectionIdAttr));
        }
        if (!sectionRoot) sectionRoot = wrap.closest && wrap.closest('[id^="shopify-section-"]');
        const summary = sectionRoot && sectionRoot.querySelector && sectionRoot.querySelector('[data-blog-pagination-summary]');
          if (summary) {
            const p = parseInt(summary.getAttribute('data-page') || '1', 10) || 1;
            const ps = parseInt(summary.getAttribute('data-pages') || '1', 10) || 1;
            wrap.setAttribute('data-page', String(p));
            wrap.setAttribute('data-pages', String(ps));
            const count = wrap.querySelector && wrap.querySelector('[data-blog-pagination-count]');
            if (count) count.textContent = String(p) + ' / ' + String(ps);
            const prevBtn = wrap.querySelector && wrap.querySelector('[data-action="prev"]');
            const nextBtn = wrap.querySelector && wrap.querySelector('[data-action="next"]');
            if (prevBtn) { const dis = p <= 1 || ps <= 1; prevBtn.toggleAttribute('disabled', dis); prevBtn.classList.toggle('pointer-events-none', dis); prevBtn.classList.toggle('opacity-40', dis); }
            if (nextBtn) { const dis = p >= ps || ps <= 1; nextBtn.toggleAttribute('disabled', dis); nextBtn.classList.toggle('pointer-events-none', dis); nextBtn.classList.toggle('opacity-40', dis); }
          }
        } catch (_) {}

        const explicitId = (wrap.getAttribute && wrap.getAttribute('data-section-id')) || '';
        let sid = null;
        if (explicitId) {
          sid = explicitId.replace(/^shopify-section-/, '');
        } else {
          const sectionWrap = wrap.closest && wrap.closest('[id^="shopify-section-"]');
          sid = sectionWrap && sectionWrap.id ? sectionWrap.id.replace('shopify-section-', '') : null;
        }
        if (sid && window.store && typeof window.store.refreshSections === 'function') {
          window.store.refreshSections([sid]);
        } else {
          window.location.assign(url.toString());
        }
      } catch (_) {
        window.location.assign(url.toString());
      }
    } catch (e) {
      store.log('[warn][blog.pagination] error', e);
    }
  }, true);

  // Sync on browser navigation (back/forward): refresh sections that include pagination controls
  try {
    window.addEventListener('popstate', () => {
      try {
        const pagers = Array.from(document.querySelectorAll('[data-blog-pagination]'));
        if (!pagers.length) return;
        const ids = Array.from(new Set(pagers.map((el) => {
          const wrap = el.closest && el.closest('[id^="shopify-section-"]');
          return wrap && wrap.id ? wrap.id.replace('shopify-section-', '') : null;
        }).filter(Boolean)));
        if (ids.length && window.store && typeof window.store.refreshSections === 'function') {
          window.store.refreshSections(ids);
        }
      } catch (_) {}
    });
  } catch (_) {}

  // After each section refresh, sync pagination controls from provider summaries within those sections
  try {
    const syncPager = (root) => {
      try {
        const sections = Array.isArray(root) ? root : [root || document];
        sections.forEach((scope) => {
          const summaries = Array.from(scope.querySelectorAll('[data-blog-pagination-summary]'));
          summaries.forEach((sum) => {
            const param = (sum.getAttribute('data-param') || 'page').toLowerCase();
            const page = parseInt(sum.getAttribute('data-page') || '1', 10) || 1;
            const pages = parseInt(sum.getAttribute('data-pages') || '1', 10) || 1;
            const sectionRoot = sum.closest && sum.closest('[id^="shopify-section-"]');
            const controls = sectionRoot && sectionRoot.querySelectorAll && sectionRoot.querySelectorAll('[data-blog-pagination]');
            controls && controls.forEach && controls.forEach((wrap) => {
              wrap.setAttribute('data-param', param);
              wrap.setAttribute('data-page', String(page));
              wrap.setAttribute('data-pages', String(pages));
              const count = wrap.querySelector && wrap.querySelector('[data-blog-pagination-count]');
              if (count) count.textContent = String(page) + ' / ' + String(pages);
              const prevBtn = wrap.querySelector && wrap.querySelector('[data-action="prev"]');
              const nextBtn = wrap.querySelector && wrap.querySelector('[data-action="next"]');
              if (prevBtn) { const dis = page <= 1 || pages <= 1; prevBtn.toggleAttribute('disabled', dis); prevBtn.classList.toggle('pointer-events-none', dis); prevBtn.classList.toggle('opacity-40', dis); }
              if (nextBtn) { const dis = page >= pages || pages <= 1; nextBtn.toggleAttribute('disabled', dis); nextBtn.classList.toggle('pointer-events-none', dis); nextBtn.classList.toggle('opacity-40', dis); }
            });
          });
        });
      } catch (_) {}
    };

    // Initial sync
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => syncPager(document), { once: true });
    } else {
      syncPager(document);
    }

    // Monkey-patch refreshSections to call sync afterwards
    try {
      const prevRefresh = window.store && window.store.refreshSections;
      if (typeof prevRefresh === 'function') {
        window.store.refreshSections = async (ids) => {
          try {
            const result = await prevRefresh(ids);
            // After DOM swaps, resync within the updated sections
            const updatedRoots = (ids || []).map((id) => document.querySelector('#shopify-section-' + id)).filter(Boolean);
            syncPager(updatedRoots.length ? updatedRoots : document);
            return result;
          } catch (e) { return undefined; }
        };
      }
    } catch (_) {}
  } catch (_) {}

  // Variant select change: keep wrapper data-variant-id in sync and emit variant.selected
  document.addEventListener('change', (ev) => {
    try {
      const sel = ev.target && ev.target.closest && ev.target.closest('select[name="id"]');
      if (!sel) return;
      const wrap = sel.closest && sel.closest('[data-product-id]');
      if (!wrap) return;
      const variantId = sel.value;
      if (variantId) {
        wrap.setAttribute('data-variant-id', variantId);
        try {
          if (window.storeEvents && window.storeEvents.emit && window.storeEvents.Events && window.storeEvents.Events.variant) {
            window.storeEvents.emit(window.storeEvents.Events.variant.selected, { variantId, productId: wrap.getAttribute('data-product-id') });
          }
        } catch (_) {}
      }
    } catch (e) { store.log('[warn][variant.change] error', e); }
  }, true);

  // Sliders: handled exclusively by <store-slider> (assets/slider.js)

  // Modal delegation removed here; canonical handling is in assets/modal.js

  // Debug: log detected modals and initial state (only when store.debug is true)
  const logInitialModals = () => {
    try {
      if (!store || !store.debug) return;
      const els = Array.from(document.querySelectorAll('[data-modal-root]'));
      store.log('[modal] initial scan:', els.length);
      els.forEach((el, i) => {
        try {
          const cs = (window.getComputedStyle && window.getComputedStyle(el)) || null;
          const display = cs ? cs.display : undefined;
          store.log('[modal] modal['+i+']', {
            id: el.id || null,
            hasHiddenClass: el.classList && el.classList.contains && el.classList.contains('hidden'),
            hasOpenAttr: el.hasAttribute && el.hasAttribute('data-open'),
            display
          });
        } catch (_) {}
      });
    } catch (_) {}
  };
  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => logInitialModals(), { once: true });
    } else {
      logInitialModals();
    }
  } catch (_) {}

  // Unified dialog API
  store.dialog = store.dialog || {};
  const getEl = (idOrSel) => { try { const id = String(idOrSel || '').replace(/^#/, ''); if (!id) return null; return document.getElementById(id); } catch (_) { return null; } };
  store.dialog.open = (idOrSel) => { try { const el = getEl(idOrSel); if (!el) return; if (el.open) el.open(); else { el.classList.remove('hidden'); el.setAttribute('data-open',''); document.body.classList.add('overflow-hidden'); } } catch (_) {} };
  store.dialog.close = (idOrSel) => { try { const el = getEl(idOrSel); if (!el) return; if (el.close) el.close(); else { el.removeAttribute('data-open'); if (el.__hadHiddenInitial) el.classList.add('hidden'); document.body.classList.remove('overflow-hidden'); } } catch (_) {} };
  store.dialog.toggle = (idOrSel) => { try { const el = getEl(idOrSel); if (!el) return; el.hasAttribute('data-open') ? store.dialog.close(idOrSel) : store.dialog.open(idOrSel); } catch (_) {} };

  // (Removed) Lifecycle + click instrumentation

  // Header/menu logic lives in <navbar> component.
  
  // Contact form scroll correction: avoid hash-driven jump; keep hero/slider in view
  const handleContactFormScroll = () => {
    try {
      const form = document.querySelector('form[id^="contact-form-"]');
      if (!form) return;
      const hasFeedback = form.querySelector && form.querySelector('[role="status"], [role="alert"]');
      const hashIsContact = !!(window.location && window.location.hash && /contact[_-]?form/i.test(window.location.hash));
      if (!hasFeedback && !hashIsContact) return;
      // Remove contact hash to stop browser auto-scrolling on reload
      try {
        if (hashIsContact && window.history && window.history.replaceState) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } catch (_) {}
      // Prefer scrolling to the hero wrapper if present; otherwise keep at top
      const hero = document.getElementById('contact-hero');
      window.requestAnimationFrame(() => {
        if (hero && hero.scrollIntoView) {
          try { hero.scrollIntoView({ block: 'start' }); } catch (_) { hero.scrollIntoView(); }
        } else {
          try { window.scrollTo({ top: 0 }); } catch (_) { window.scrollTo(0, 0); }
        }
      });
    } catch (_) {}
  };
  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => handleContactFormScroll(), { once: true });
    } else {
      handleContactFormScroll();
    }
  } catch (_) {}
})();


// (Removed) Deferred media loader: we rely on native lazy-loading on iframes/videos now.


